import React, { useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Bug, Loader2 } from 'lucide-react';

/**
 * ReportBug: exposes both a floating button (visible on every page for logged-in users)
 * and a controlled dialog that can be opened externally via `openRef.current.open()`.
 */
export function ReportBugFloating() {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  if (!user) return null;
  return (
    <>
      <Button
        data-testid="report-bug-floating-btn"
        onClick={() => setOpen(true)}
        className="fixed bottom-20 md:bottom-4 right-4 z-40 rounded-full shadow-lg bg-white hover:bg-muted text-primary border border-border h-11 w-11 p-0"
        title="Report a bug"
        aria-label="Report a bug"
      >
        <Bug className="w-5 h-5" />
      </Button>
      <ReportBugDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

export function ReportBugDialog({ open, onOpenChange }) {
  const { token } = useContext(AuthContext);
  const location = useLocation();
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (description.trim().length < 5) return toast.error('Please describe the bug (at least 5 chars)');
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/bug-reports`,
        {
          description: description.trim(),
          page: location.pathname + location.search,
          user_agent: navigator.userAgent,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Thanks — we'll take a look right away.");
      setDescription('');
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit — try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="report-bug-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Bug className="w-5 h-5 text-primary" /> Report a bug</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Tell us what you were doing and what went wrong. We'll get an email and follow up.
          </p>
          <div>
            <Label>What happened?</Label>
            <Textarea
              data-testid="bug-description-input"
              required
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={"e.g. Clicked 'Apply Filters' — got a blank screen."}
              maxLength={5000}
            />
          </div>
          <div className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-2 flex items-center justify-between">
            <span>Attached: current page</span>
            <span className="font-mono">{location.pathname}</span>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="submit-bug-btn" type="submit" disabled={submitting} className="bg-primary hover:bg-primary/90">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : 'Send Report'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
